import React, { useReducer } from "react";
import { BrowserRouter as Router, Redirect, Route, Switch } from "react-router-dom";
import { CardElement, useStripe, useElements} from "@stripe/react-stripe-js";
import { ReactComponent as StripeLogo } from './StripeLogo.svg';
import { ReactComponent as PoweredByStripe } from './powered_by_stripe.svg';

import NavButton from './NavButton.js';
import SubmitButton from './SubmitButton.js';
import ColourSelector from './ColourSelector.js';

import config from "./config";

import "./MyForm.css";
import "./pure-buttons.css";
import "./circular-progress.css";

const initialState = (process.env.NODE_ENV === 'development') ?
      {
        accepted: true,
        billing_address_line1: '1 Derwy',
        billing_address_line2: '',
        billing_county: 'Shire',
        billing_name: 'Mal Practice',
        billing_phone: '0777',
        billing_postcode: 'GL1 1GL',
        billing_town: 'Toon',
        colour: '',
        email: 'mal@bad.com',
        errorMessage: '',
        isCardComplete: false,
        isDone: false,
        isBusy: false,
        ship_to_billing_address: true,
        shipping_address_line1: '1 Nonsuch Place',
        shipping_address_line2: '',
        shipping_county: 'Denyshire',
        shipping_name: 'Mr Smith',
        shipping_phone: '0997',
        shipping_postcode: 'GL66',
        shipping_town: 'Nonsuch',
      } :
      {
        accepted: false,
        billing_address_line1: '',
        billing_address_line2: '',
        billing_county: '',
        billing_name: '',
        billing_phone: '',
        billing_postcode: '',
        billing_town: '',
        colour: '',
        email: '',
        errorMessage: '',
        isCardComplete: false,
        isDone: false,
        isBusy: false,
        ship_to_billing_address: true,
        shipping_address_line1: '',
        shipping_address_line2: '',
        shipping_county: '',
        shipping_name: '',
        shipping_phone: '',
        shipping_postcode: '',
        shipping_town: '',
      };

function reducer(state, { field, value }) {
  return {
    ...state,
    [field]: value
  };
}

const MyForm = () => {
  const stripe = useStripe();
  const elements = useElements();

  const [state, dispatch] = useReducer(reducer, initialState);

  const onChange = (e) => {
    const target = e.currentTarget;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    dispatch({ field: target.name, value: value });
  };

  const onCardFieldChange = (response) => {
    dispatch({ field: 'isCardComplete', value: response.complete });
  };

  const billingDetails = () => (
    {
      name: state.billing_name,
      phone: state.billing_phone,
      email: state.email,
      address: {
      city: state.billing_town,
        line1: state.billing_address_line1,
        line2: state.billing_address_line2,
        postal_code: state.billing_postcode,
        state: state.billing_county,
      }
    }
  );

  const orderInfo = () => {

    let orderInfo = {
      billing: {
        name: state.billing_name,
        phone: state.billing_phone,
        address: {
          city: state.billing_town,
          country: 'United Kingdom',
          line1: state.billing_address_line1,
          line2: state.billing_address_line2,
          postal_code: state.billing_postcode,
          state: state.billing_county,
        },
      },
      email: state.email,
      product: {
        colour: state.colour,
        description: 'Free Trial'
      }
    };

    if (!state.ship_to_billing_address) {
      orderInfo.shipping = {
        name: state.shipping_name,
        phone: state.shipping_phone,
        address: {
          city: state.shipping_town,
          country: 'United Kingdom',
          line1: state.shipping_address_line1,
          line2: state.shipping_address_line2,
          postal_code: state.shipping_postcode,
          state: state.shipping_county,
        },
      };
    }

    return orderInfo;
  };


  const saveOrder = async () => {

    const cardElement = elements.getElement('card');

    const res = await fetch(config.stripe.setupIntentUrl, {
      method: 'POST'
    });
    let intent = await res.json();

    // What about errors from setuIntentUrl?

    if (!intent || !intent.client_secret) {
      throw new Error('Error');
    }

    // This may trigger a 3D Secure authentication challenge.
    const { setupIntent, error: cardSetupError } = await stripe.confirmCardSetup(
      intent.client_secret,
      {
        payment_method: {
          billing_details: billingDetails(),
          card: cardElement
        }
      }
    );

    if (cardSetupError) {
      throw new Error(cardSetupError.message);
    }

    if (setupIntent) {
      let res2 = await fetch(config.stripe.createCustomerUrl, {
        body: JSON.stringify({ payment_method: setupIntent.payment_method, 'orderInfo': orderInfo() }),
        method: 'POST',
      });

      let json = await res2.json();
      if (json.error) {
        throw new Error(json.error);
      }
    }
  };


  const onSubmit = (event) => {
    // Block native form submission.
    event.preventDefault();

    dispatch({ field: 'errorMessage', value: '' });

    if (!state.billing_name) {
      return;
    }

    if (!stripe || !elements) {
      dispatch({ field: 'errorMessage', value: 'Stripe not loaded' })
    }

    dispatch({ field: 'isBusy', value: true });

    saveOrder().then(() => {
      dispatch({ field: 'isDone', value: true });
      dispatch({ field: 'isBusy', value: false });
    })
    .catch((e) => {
      dispatch({ field: 'errorMessage', value: e.message });
      dispatch({ field: 'isBusy', value: false });
    });

  };

  const isPartAComplete = () => (
    state.accepted &&
    state.colour !== ""
  );

  const isPartBComplete = () => (
    state.billing_name !== "" &&
    state.billing_address_line1 !== "" &&
    state.billing_postcode !== "" &&
    state.email !== ""
  );

  const isPartCComplete = () => (
    state.shipping_name !== "" &&
    state.shipping_address_line1 !== "" &&
    state.shipping_postcode !== ""
  );

  const isDone = () => state.isDone;

  const isBusy = () => state.isBusy;

  const isFormValid = () => (
    isPartAComplete() &&
    isPartBComplete() &&
    state.isCardComplete
  );

  const renderPartA = () => (
    <React.Fragment>
      <section>
        <p>The trial stool might be any colour. If we are able to send you the first trial of a new stool, and you choose a colour here, you will have the option of buying it:</p>

        <h3>Choose fabric colour:</h3>
        <ColourSelector colour={state.colour} onChange={onChange} />

        <p>(Colours may vary depending how your screen is calibrated. Hover over a colour for a description.)</p>
        <hr /><br />

        <p>
          <label>
            <input id="accepted" name="accepted" type="checkbox" className="pos-checkbox" checked={state.accepted} onChange={onChange} />
            I agree to make reasonable arrangements to deliver to the next recipient on a day that is convenient for them, as described on the previous page, at my expense. (If I then order <i>pos</i>Chair 1 this will be deducted from the all inclusive price of £320).
          </label>
        </p>

      </section>
      <div className="pos-container-aligned">
        <NavButton to="/form/b" className="pure-button pure-button-primary" disabled={!isPartAComplete()}>Next</NavButton>
      </div>
      <section>
        <p>Your order data is stored using the
          <a href="https://stripe.com" rel="noopener noreferrer" target="_blank">
            <StripeLogo className="stripe-logo" />
          </a>
          payment gateway.
          We will never charge your card unless: You tell us you want to pay a courier surcharge, You want to order, and ask us to use this card, Or you fail to send the stool on or show us a courier’s collection
          receipt, when we'll have to charge its estimated value.
          (We’ve never had to do this, and would make repeated attempts to contact you by email and phone first).</p>
      </section>
    </React.Fragment>
  );

  const renderPartB = () => (
    <React.Fragment>
      <fieldset className="pos-fieldset pos-centred">
        <legend className="pos-legend">Billing Address</legend>
        <div className="form-container">

          <label className="pos-label form-content" htmlFor="billing_name">*Name</label>
          <input className="pos-input form-content" id="billing_name" name="billing_name" type="text" value={state.billing_name} onChange={onChange} />

          <label className="pos-label form-content" htmlFor="email">*Email</label>
          <input className="pos-input form-content" id="email" name="email" type="email" value={state.email} onChange={onChange} />

          <label className="pos-label form-content" htmlFor="billing_phone">Phone</label>
          <input className="pos-input form-content" id="billing_phone" name="billing_phone" type="tel" value={state.billing_phone} onChange={onChange} />

          <label className="pos-label form-content" htmlFor="billing_postcode">*Post Code</label>
          <input className="pos-input form-content" id="billing_postcode" name="billing_postcode" type="text" value={state.billing_postcode} onChange={onChange} />

          <label className="pos-label form-content" htmlFor="billing_address_line1">*Address Line 1</label>
          <input className="pos-input form-content" id="billing_address_line1" name="billing_address_line1" type="text" value={state.billing_address_line1} onChange={onChange} />

          <label className="pos-label form-content" htmlFor="billing_address_line2">Address Line 2</label>
          <input className="pos-input form-content" id="billing_address_line2" name="billing_address_line2" type="text" value={state.billing_address_line2} onChange={onChange} />

          <label className="pos-label form-content" htmlFor="billing_town">Town / City</label>
          <input className="pos-input form-content" id="billing_town" name="billing_town" type="text" value={state.billing_town} onChange={onChange} />

          <label className="pos-label form-content" htmlFor="billing_county">County</label>
          <input className="pos-input form-content" id="billing_county" name="billing_county" type="text" value={state.billing_county} onChange={onChange} />

          <label className="pos-label form-content" htmlFor="billing_country">Country</label>
          <input className="pos-input form-content" id="billing_country" disabled={true} value="United Kingdom" />

          <label>
            <input id="ship_to_billing_address" name="ship_to_billing_address" type="checkbox" className="pos-checkbox" checked={state.ship_to_billing_address} onChange={onChange} />
            Deliver to this address
          </label>
        </div>
      </fieldset>
      <div className="pos-container-aligned">
        <NavButton to={state.ship_to_billing_address ? "/form/d" : "/form/c"} className="pure-button pure-button-primary pos-button" disabled={!isPartBComplete()}>Next</NavButton>
      </div>
    </React.Fragment>
  );

  const renderPartC = () => (
    <React.Fragment>
      <fieldset className="pos-fieldset pos-centred">
        <legend className="pos-legend">Delivery Address</legend>
        <div className="form-container">

          <label className="pos-label form-content" htmlFor="shipping_name">*Name</label>
          <input className="pos-input form-content" id="shipping_name" name="shipping_name" type="text" value={state.shipping_name} onChange={onChange} />

          <label className="pos-label form-content" htmlFor="shipping_phone">Phone</label>
          <input className="pos-input form-content" id="shipping_phone" name="shipping_phone" type="tel" value={state.shipping_phone} onChange={onChange} />

          <label className="pos-label form-content" htmlFor="shipping_postcode">*Post Code</label>
          <input className="pos-input form-content" id="shipping_postcode" name="shipping_postcode" type="text" value={state.shipping_postcode} onChange={onChange} />

          <label className="pos-label form-content" htmlFor="shipping_address_line1">*Address Line 1</label>
          <input className="pos-input form-content" id="shipping_address_line1" name="shipping_address_line1" type="text" value={state.shipping_address_line1} onChange={onChange} />

          <label className="pos-label form-content" htmlFor="shipping_address_line2">Address Line 2</label>
          <input className="pos-input form-content" id="shipping_address_line2" name="shipping_address_line2" type="text" value={state.shipping_address_line2} onChange={onChange} />

          <label className="pos-label form-content" htmlFor="shipping_town">Town / City</label>
          <input className="pos-input form-content" id="shipping_town" name="shipping_town" type="text" value={state.shipping_town} onChange={onChange} />

          <label className="pos-label form-content" htmlFor="shipping_county">County</label>
          <input className="pos-input form-content" id="shipping_county" name="shipping_county" type="text" value={state.shipping_county} onChange={onChange} />

          <label className="pos-label form-content" htmlFor="shipping_country">Country</label>
          <input className="pos-input form-content" id="shipping_country" disabled={true} value="United Kingdom" />

        </div>
      </fieldset>
      <div className="pos-container-aligned">
        <NavButton to="/form/d" className="pure-button pure-button-primary pos-button" disabled={!isPartCComplete()}>Next</NavButton>
      </div>
    </React.Fragment>
  );

  const renderPartD = () => (
    <React.Fragment>
      <fieldset className="pos-fieldset pos-centred" disabled={isBusy()}>
        <legend className="pos-legend">Card Details</legend>

        <CardElement
          className="card-field"
          onChange={onCardFieldChange}
          options={{
            hidePostalCode: true,
            style: {
              base: { fontSize: "18px", fontFamily: '"Open Sans", sans-serif' }
            }
          }}
        />
      </fieldset>

      <div className="pos-container-aligned">
        <SubmitButton
          className="pure-button pure-button-primary pos-button pos-button-submit"
          disabled={!isFormValid() || isBusy()}
        >Submit</SubmitButton>
      </div>

      <div className="stripe-badge">
        <a href="https://stripe.com" rel="noopener noreferrer" target="_blank">
          <PoweredByStripe />
        </a>
      </div>

      <div>{state.errorMessage}</div>
      <div className="pos-container-aligned"> {isBusy() && <progress className="pure-material-progress-circular" />} </div>
    </React.Fragment>
  );

  const renderPartE = () => {
    if (!isPartAComplete()) {
      return <Redirect to="/form/a" />;
    } else {
      return (
        <div>
          <h1>Done</h1>
          Thank You, we've received your trial request. As soon as a trial stool
          is becoming available we'll contact you to arrange delivery. This may
          show on your card statement as a pending payment of £0.
        </div>
      );
    }
  };

  return (
    <form onSubmit={onSubmit} className="pos-form">
      <Router basename="/our-seats/poschair1/book">
        <Switch>
          <Redirect from="/" exact={true} to="/form/a" />
          <Route path="/form/a" exact={true} render={renderPartA} />
          {
            !isPartAComplete() && <Redirect from="/form" to="/form/a" />
          }
          <Route path="/form/b" exact={true} render={renderPartB} />
          <Route path="/form/c" exact={true} render={renderPartC} />
          {
            isDone() && <Redirect from="/form/d" to="/done" />
          }
          <Route path="/form/d" exact={true} render={renderPartD} />
          <Route path="/done" render={renderPartE} />
        </Switch>
      </Router>
    </form>
  );
};

export default MyForm;
